<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { getApiErrorMessage } from '@/utils/apiError'

import userService from '../../services/userService'

definePage({
  meta: {
    title: 'pages.user.forgotPassword.title',
    requiresAuth: false,
  },
})

const email = ref('')
const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const { t } = useI18n()

const router = useRouter()

const requestPasswordReset = async () => {
  if (!email.value) {
    errorMessage.value = t('pages.user.forgotPassword.messages.emailRequired')
    return
  }

  try {
    loading.value = true
    errorMessage.value = ''
    const success = await userService.forgotPassword(email.value)

    if (success) {
      successMessage.value = t('pages.user.forgotPassword.messages.success')
      email.value = ''
    } else {
      errorMessage.value = t('pages.user.forgotPassword.messages.genericError')
    }
  } catch (error: unknown) {
    console.error('Forgot password error:', error)
    errorMessage.value = getApiErrorMessage(
      error,
      t,
      t('pages.user.forgotPassword.messages.genericError'),
    )
  } finally {
    loading.value = false
  }
}

const navigateToLogin = () => {
  router.push('/user/login')
}
</script>

<template>
  <div style="max-width: 400px" class="mycard px-4 py-2">
    <div class="mb-4 flex flex-row justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-200">
        {{ t('pages.user.forgotPassword.title') }}
      </h1>
    </div>
    <p class="mb-4 text-muted">
      {{ t('pages.user.forgotPassword.intro') }}
    </p>

    <AppAlert v-if="errorMessage" color="error" :closable="false" class="mb-4">
      {{ errorMessage }}
    </AppAlert>
    <AppAlert v-if="successMessage" color="success" :closable="false" class="mb-4">
      {{ successMessage }}
    </AppAlert>

    <div class="flex flex-col gap-3">
      <AppFloatField variant="on">
        <UInput id="email" v-model="email" class="w-full" autocomplete="email" />
        <label for="email">{{ t('fields.user.email') }}</label>
      </AppFloatField>

      <div class="mt-2 flex flex-row gap-2">
        <UButton
          :label="t('pages.user.forgotPassword.submit')"
          class="w-full"
          :loading="loading"
          :disabled="!email"
          @click="requestPasswordReset"
        />
      </div>

      <div class="mt-3 flex flex-row justify-center">
        <a
          class="cursor-pointer font-semibold text-blue-700 dark:text-blue-400"
          @click="navigateToLogin"
        >
          {{ t('pages.user.forgotPassword.backToLogin') }}
        </a>
      </div>
    </div>
  </div>
</template>
